const COMPARE_LIST_KEY = 'compareList';
const MAX_COMPARE_ITEMS = 3;
const COMPARE_SESSION_COOKIE = 'compare_session_active';

// Clear the localStorage if it's a new session (no session cookie existent)
if (!document.cookie.includes(`${COMPARE_SESSION_COOKIE}=true`)) {
  localStorage.removeItem(COMPARE_LIST_KEY);
  // Set a session cookie
  document.cookie = `${COMPARE_SESSION_COOKIE}=true; path=/; SameSite=Strict`;
}

// Enable live cross-tab sync
window.addEventListener('storage', (e) => {
  if (e.key === COMPARE_LIST_KEY) {
    const newList = JSON.parse(e.newValue || '[]');
    document.dispatchEvent(new CustomEvent('compareListUpdated', { detail: newList }));
  }
});

class CompareState {
  static getList() {
    return JSON.parse(localStorage.getItem(COMPARE_LIST_KEY) || '[]');
  }

  static setList(list) {
    localStorage.setItem(COMPARE_LIST_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('compareListUpdated', { detail: list }));
  }

  static add(product) {
    const list = this.getList();
    if (list.length >= MAX_COMPARE_ITEMS) return false;
    if (list.some(p => p.handle === product.handle)) return false;
    list.push(product);
    this.setList(list);
    return true;
  }

  static remove(handle) {
    const list = this.getList().filter(p => p.handle !== handle);
    this.setList(list);
  }

  static clear() {
    this.setList([]);
  }

  static has(handle) {
    return this.getList().some(p => p.handle === handle);
  }
}

class CompareButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    
    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;
    this.productTitle = this.dataset.productTitle;
    this.productImage = this.dataset.productImage;
    this.productVendor = this.dataset.productVendor;
    this.productVintage = this.dataset.vintage;
    this.productPrice = this.dataset.price;
    
    this.button.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('compareListUpdated', this.updateState.bind(this));
    this.updateState();
  }
  disconnectedCallback() {
    if (!this.button) return;
    this.button.removeEventListener('click', this.handleClick.bind(this));
    document.removeEventListener('compareListUpdated', this.updateState.bind(this));
  }

  updateState() {
    if (!this.button) return;
    this.button.classList.toggle('selected', CompareState.has(this.productHandle));
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
      
    if (CompareState.has(this.productHandle)) {
      CompareState.remove(this.productHandle);
      return;
    }

    const success = CompareState.add({
      handle: this.productHandle,
      url: this.productUrl,
      title: this.productTitle,
      image: this.productImage,
      vendor: this.productVendor,
      vintage: this.productVintage,
      price: this.productPrice
    });

    if (!success) {
      alert(window.KROWN?.settings.locales.compare_add_limit || 'Sie können maximal 3 Produkte vergleichen.');
    }
  }
}
customElements.define('compare-button', CompareButton);

class CompareModal extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    this.container = this.querySelector('.compare-modal-container');
    this.itemsContainer = this.querySelector('.compare-items');
    this.clearAllBtn = this.querySelector('.compare-clear-all-btn');
    this.openMobileModal = document.querySelector('.compare-head');
    this.compareNumberEl = this.querySelector('#compare-number');
    this.mobileCompareBtn = this.querySelector('.compare-navigations.only-mobile .compare-add-btn');
    this.toTop = document.querySelector('#go-top');
    this.placeholdersHTML = [];
    
    if(this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', () => CompareState.clear());
      if(this.openMobileModal && CompareState.getList().length === 0) {
        this.classList.remove('is-open');
      }
    }
    
    document.addEventListener('compareListUpdated', (e) => this.render(e.detail));
    if (this.placeholdersHTML.length === 0) {
      for (let index = 0; index <= 2; index++) {
        const placeholder = document.querySelector('[data-placeholder-' + index + ']');
        if (placeholder) {
          this.placeholdersHTML[index] = placeholder.innerHTML;
        }
      }
    }
    if(this.openMobileModal) {
      this.openMobileModal.addEventListener('click', () => {
        this.classList.toggle('is-open');
      });
    }
    this.render(CompareState.getList());
  }
  disconnectedCallback() {
    document.removeEventListener('compareListUpdated', (e) => this.render(e.detail));
  }
  
  render(list) {
    if (list.length === 0) {
      this.style.display = 'none';
      if (this.toTop) {
        this.toTop.style.transform = '';
      }
      return;
    }
    this.style.display = 'flex';
    if (this.toTop) {
      this.toTop.style.transform = 'translateY(-100%)';
    }
    if (this.compareNumberEl) {
      this.compareNumberEl.textContent = window.KROWN?.settings.locales.product_compare.replace('{{ number }}', list.length);
    }
    if (this.mobileCompareBtn) {
      this.mobileCompareBtn.classList.toggle('hidden', list.length < 2);
    }
    
    if(this.itemsContainer) {
      for (let index = 0; index <= 2; index++) {
        const placeholderEl = this.container.querySelector('[data-placeholder-' + index + ']');
        if (placeholderEl) {
          if (list[index]) {
            const item = list[index];
            placeholderEl.innerHTML = `
              <div class="compare-item">
                ${item.image ? `<img src="${item.image}" alt="${item.title}" width="90" height="auto" style="object-fit:cover; border-radius:4px;">` : ''}
                <div class="item-info">
                  <div class="wine-info">
                    <span class="text-size--xsmall">${item.vintage || ''}</span>
                    <span class="h5">${item.title}</span>
                    <span class="text-size--xsmall">${item.vendor || ''}</span>
                  </div>
                  <span class="text-size--regular">${item.price}</span>
                </div>
                <button type="button" class="compare-remove-item" data-handle="${item.handle}">
                  <svg width="50" height="20" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1964_26735)">
                      <path d="M23.0092 15.4316C22.7746 15.4316 22.5842 15.2412 22.5842 15.0066V7.07322C22.5842 6.83859 22.7746 6.64822 23.0092 6.64822C23.2438 6.64822 23.4342 6.83859 23.4342 7.07322V15.0066C23.4342 15.2412 23.2438 15.4316 23.0092 15.4316Z" fill="black"/>
                      <path d="M20 15.4316C19.7654 15.4316 19.575 15.2412 19.575 15.0066V7.07322C19.575 6.83859 19.7654 6.64822 20 6.64822C20.2347 6.64822 20.425 6.83859 20.425 7.07322V15.0066C20.425 15.2412 20.2347 15.4316 20 15.4316Z" fill="black"/>
                      <path d="M16.9908 15.4316C16.7561 15.4316 16.5658 15.2412 16.5658 15.0066V7.07322C16.5658 6.83859 16.7561 6.64822 16.9908 6.64822C17.2254 6.64822 17.4158 6.83859 17.4158 7.07322V15.0066C17.4158 15.2412 17.2254 15.4316 16.9908 15.4316Z" fill="black"/>
                      <path d="M10.65 2.9649H16.4583V1.80279C16.4583 0.837681 17.2441 0.0518799 18.2092 0.0518799H21.7908C22.7559 0.0518799 23.5417 0.837681 23.5417 1.80279V2.9649H29.35C29.5847 2.9649 29.775 3.15527 29.775 3.3899C29.775 3.62454 29.5847 3.8149 29.35 3.8149H26.9417V17.0839C26.9417 18.2039 26.0308 19.1148 24.9108 19.1148H15.0881C13.968 19.1148 13.0571 18.2039 13.0571 17.0839L13.0582 3.8149H10.6499C10.4153 3.8149 10.2249 3.62454 10.2249 3.3899C10.2249 3.15527 10.4153 2.9649 10.6499 2.9649H10.65ZM22.6916 1.80279C22.6916 1.30584 22.2877 0.90188 21.7907 0.90188H18.208C17.711 0.90188 17.3071 1.30584 17.3071 1.80279V2.88521H22.6904L22.6916 1.80279ZM13.9083 17.0841C13.9083 17.7349 14.4373 18.2651 15.0892 18.2651H24.9108C25.5615 18.2651 26.0917 17.736 26.0917 17.0841V3.81516H13.9084L13.9083 17.0841Z" fill="black"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_1964_26735">
                        <rect width="20" height="19.1667" fill="white" transform="matrix(-1 0 0 1 30 0)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            `;
          } else {
            placeholderEl.innerHTML = this.placeholdersHTML[index] || '';
          }
        } 
      }
      this.itemsContainer.querySelectorAll('.compare-remove-item').forEach(btn => {
        btn.addEventListener('click', (e) => {
          CompareState.remove(e.currentTarget.dataset.handle);
          if(CompareState.getList().length === 0) {
            this.classList.remove('is-open');
          }
        });
      });
    }
  }
}
customElements.define('compare-modal', CompareModal);

class CompareGrid extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    this.wrapper = this.querySelector('.compare-grid-wrapper');
    this.emptyMsg = this.querySelector('.compare-grid-empty');
    this.compareProducts = this.querySelector('.compare-grid-header');
    document.addEventListener('compareListUpdated', (e) => this.render(e.detail));

    this.render(CompareState.getList());
    this.initScrollSync();
  }
  disconnectedCallback() {
    document.removeEventListener('compareListUpdated', (e) => this.render(e.detail));
  }
  
  initScrollSync() {
    this.headerSlider = this.querySelector('.compare-grid-header');
    this.rowSliders = Array.from(this.querySelectorAll('.compare-row-right'));
    this.allSliders = [this.headerSlider, ...this.rowSliders].filter(Boolean);

    this.activeDriver = null;

    const setActiveDriver = (e) => {
      if (window.innerWidth > 768) return;
      this.activeDriver = e.currentTarget;
    };

    this.allSliders.forEach(slider => {
      slider.addEventListener('touchstart', setActiveDriver, { passive: true });
      slider.addEventListener('mouseenter', setActiveDriver, { passive: true });
      slider.addEventListener('focusin', setActiveDriver, { passive: true });

      slider.addEventListener('scroll', () => {
        if (window.innerWidth > 768) return;
        
        if (this.activeDriver !== slider) return;

        const maxScroll = slider.scrollWidth - slider.clientWidth;
        const scrollPercentage = maxScroll > 0 ? (slider.scrollLeft / maxScroll) : 0;

        this.allSliders.forEach(other => {
          if (other !== slider) {
            const otherMaxScroll = other.scrollWidth - other.clientWidth;
            other.scrollLeft = scrollPercentage * otherMaxScroll;
          }
        });
      }, { passive: true });
    });
  }

  // Just makes sure the value we get from the JSON is properly formatted or if none is given a placeholder is set
  // so i.e. an array has a space between commatas
  formatValue(val) {
    if (val === null || val === undefined || val === '') return '-';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') {
       if (val.value !== undefined) return Array.isArray(val.value) ? val.value.join(', ') : val.value;
       return JSON.stringify(val);
    }
    return val;
  }

  async render(list) {
    if (!this.wrapper) return;
    const innerWidth = window.innerWidth;
    let isMobile = false;
    if (innerWidth <= 768) {
      isMobile = true;
    }

    this.querySelectorAll('.compare-grid-product .compare-card-header').forEach(el => el.remove());
    this.querySelectorAll('.compare-row-cell').forEach(el => el.innerHTML = '<span class="compare-loading-val">-</span>');
    
    if (list.length === 0) {
      this.wrapper.style.display = 'none';
      if(this.emptyMsg) this.emptyMsg.style.display = 'block';
      return;
    }
    
    this.wrapper.style.display = 'flex';
    if(this.emptyMsg) this.emptyMsg.style.display = 'none';

    for(let i = 0; i < 3; i++) {
      const placeholder = this.querySelector(`[data-placeholder-${i}]`)?.closest('.compare-item-header-placeholder');
      if (placeholder) {
        placeholder.style.display = (i < list.length) ? 'none' : 'block';
      }
    }

    // Fetch and populate columns
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      try {
        const sectionUrl = `${item.url}${item.url.includes('?') ? '&' : '?'}section_id=compare-card`;
        const headerPlaceholder = this.querySelector(`.compare-grid-product[data-product-index="${i}"]`);
        if (headerPlaceholder) {
          headerPlaceholder.insertAdjacentHTML('beforeend', `<div class="compare-card-header compare-item"><div class="compare-loading">${KROWN?.settings.locales.loading_data || 'Loading product data...'}</div></div>`);
        }

        const response = await fetch(sectionUrl);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const scriptData = doc.querySelector('#compare-data');
        if (!scriptData) throw new Error('No JSON data found');
        const data = JSON.parse(scriptData.textContent);

        const loadingHeader = headerPlaceholder.querySelector('.compare-card-header');
        if(loadingHeader) loadingHeader.remove();

        const headerHtml = `
          <div class="compare-card-header" data-product-handle="${item.handle}">
            <div class="compare-item">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" width="90" height="auto" style="object-fit:cover; border-radius:4px;">` : ''}
              <div class="item-info">
                <div class="wine-info">
                  <span class="text-size--xsmall">${item.vintage || ''}</span>
                  <span class="h5">${item.title}</span>
                  <span class="text-size--xsmall">${item.vendor || ''}</span>
                </div>
                <span class="text-size--regular">${item.price}</span>
              </div>
              <button type="button" class="compare-remove-item compare-grid-remove" data-handle="${item.handle}">
                <svg width="50" height="20" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_1964_26735)">
                    <path d="M23.0092 15.4316C22.7746 15.4316 22.5842 15.2412 22.5842 15.0066V7.07322C22.5842 6.83859 22.7746 6.64822 23.0092 6.64822C23.2438 6.64822 23.4342 6.83859 23.4342 7.07322V15.0066C23.4342 15.2412 23.2438 15.4316 23.0092 15.4316Z" fill="black"/>
                    <path d="M20 15.4316C19.7654 15.4316 19.575 15.2412 19.575 15.0066V7.07322C19.575 6.83859 19.7654 6.64822 20 6.64822C20.2347 6.64822 20.425 6.83859 20.425 7.07322V15.0066C20.425 15.2412 20.2347 15.4316 20 15.4316Z" fill="black"/>
                    <path d="M16.9908 15.4316C16.7561 15.4316 16.5658 15.2412 16.5658 15.0066V7.07322C16.5658 6.83859 16.7561 6.64822 16.9908 6.64822C17.2254 6.64822 17.4158 6.83859 17.4158 7.07322V15.0066C17.4158 15.2412 17.2254 15.4316 16.9908 15.4316Z" fill="black"/>
                    <path d="M10.65 2.9649H16.4583V1.80279C16.4583 0.837681 17.2441 0.0518799 18.2092 0.0518799H21.7908C22.7559 0.0518799 23.5417 0.837681 23.5417 1.80279V2.9649H29.35C29.5847 2.9649 29.775 3.15527 29.775 3.3899C29.775 3.62454 29.5847 3.8149 29.35 3.8149H26.9417V17.0839C26.9417 18.2039 26.0308 19.1148 24.9108 19.1148H15.0881C13.968 19.1148 13.0571 18.2039 13.0571 17.0839L13.0582 3.8149H10.6499C10.4153 3.8149 10.2249 3.62454 10.2249 3.3899C10.2249 3.15527 10.4153 2.9649 10.6499 2.9649H10.65ZM22.6916 1.80279C22.6916 1.30584 22.2877 0.90188 21.7907 0.90188H18.208C17.711 0.90188 17.3071 1.30584 17.3071 1.80279V2.88521H22.6904L22.6916 1.80279ZM13.9083 17.0841C13.9083 17.7349 14.4373 18.2651 15.0892 18.2651H24.9108C25.5615 18.2651 26.0917 17.736 26.0917 17.0841V3.81516H13.9084L13.9083 17.0841Z" fill="black"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_1964_26735">
                      <rect width="20" height="19.1667" fill="white" transform="matrix(-1 0 0 1 30 0)"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div class="compare-atc">
              <form method="post" action="/cart/add" data-type="add-to-cart-form">
                <input type="hidden" name="id" value="${data.variant_id}" />
                <button type="submit" class="button button--solid button--regular button--fullwidth" ${!data.available ? 'disabled' : ''}>
                  ${data.available ? (window.KROWN?.settings.locales.products_add_to_cart_button || 'Add to Cart') : (window.KROWN?.settings.locales.products_sold_out_variant || 'Sold Out')}
                </button>
              </form>
            </div>
          </div>
        `;
        headerPlaceholder.insertAdjacentHTML('beforeend', headerHtml);

        this.querySelectorAll(`.compare-row-cell[data-product-index="${i}"]`).forEach(cell => {
          let key = cell.dataset.key;
          key = key.trim();
          let valStr = '-';
          if(key.startsWith('product.')) {
            const prop = key.split('.')[1];
            valStr = this.formatValue(data[prop]);
          } else {
            const parts = key.split('.');
            if (parts.length == 2) {
              const nameSpace = parts[0];
              const key = parts.slice(1).join('.');
              if(data.metafields && data.metafields[nameSpace] && data.metafields[nameSpace][key] !== undefined) {
                valStr = this.formatValue(data.metafields[nameSpace][key]);
              }
            }
          }
          cell.dataset.productHandle = item.handle;
          cell.innerHTML = valStr;
        });
      } catch (err) {
        console.error("Compare Grid error:", err);
        const headerPlaceholder = this.querySelector(`.compare-grid-product[data-product-index="${i}"]`);
        if (headerPlaceholder) {
          headerPlaceholder.insertAdjacentHTML('beforeend', `<p>${window.KROWN?.settings.locales.error_loading_data || 'Error loading data.'}</p>`);
        }
      }
    }
    
    this.querySelectorAll('.compare-grid-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        CompareState.remove(e.currentTarget.dataset.handle);
      });
    });
  }
}
customElements.define('compare-grid', CompareGrid);

class HistoryBackButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.goBack.bind(this));
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.goBack.bind(this));
  }

  goBack(event) {
    event.preventDefault();
    const currentHost = window.location.host;
    if (document.referrer && document.referrer.includes(currentHost)) {
      window.location.href = document.referrer;
    } 
    else if (window.history.length > 1) {
      window.history.back();
    } 
    else {
      window.location.href = '/';
    }
  }
}

customElements.define('history-back-button', HistoryBackButton);